const STORAGE_PREFIX = 'ts:portfolio:beltLifetime:v1';
const DEFAULT_CHUNK_SIZE = 50_000;
const MIN_CHUNK_SIZE = 5_000;
const USDC_DECIMALS = 6;

const EVENT_DEFS = {
  erc4626: {
    name: 'Deposit',
    signature: 'event Deposit(address indexed caller, address indexed owner, uint256 assets, uint256 shares)',
    ownerField: 'owner',
    topicFilter: (ownerTopic, topic0) => [topic0, null, ownerTopic],
  },
  depositUser: {
    name: 'Deposit',
    signature: 'event Deposit(address indexed user, uint256 assets, uint256 shares, uint256 fee)',
    ownerField: 'user',
    topicFilter: (ownerTopic, topic0) => [topic0, ownerTopic],
  },
  depositedUser: {
    name: 'Deposited',
    signature: 'event Deposited(address indexed user, uint256 assets, uint256 shares, uint256 fee)',
    ownerField: 'user',
    topicFilter: (ownerTopic, topic0) => [topic0, ownerTopic],
  },
};

const BELT_EVENT_BY_KEY = {
  white: 'erc4626',
  green: 'erc4626',
  blue: 'depositedUser',
  black: 'depositUser',
};

function getEthers() {
  const eth = window?.ethers;
  if (!eth) {
    throw new Error('ethers_unavailable');
  }
  return eth;
}

function makeCacheKey(chain, vaultAddress, userAddress) {
  return `${STORAGE_PREFIX}:${chain}:${vaultAddress}:${userAddress}`;
}

function loadCache(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || data.version !== 1) return null;
    return data;
  } catch {
    return null;
  }
}

function saveCache(key, data) {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch {
    // ignore storage failures
  }
}

function normalizeAddress(ethers, addr) {
  try {
    return ethers.getAddress(addr).toLowerCase();
  } catch {
    return String(addr || '').toLowerCase();
  }
}

function toOwnerTopic(ethers, addrLower) {
  return ethers.zeroPadValue(addrLower, 32);
}

function resolveDepositEvent(belt) {
  const key = belt?.key;
  const type = BELT_EVENT_BY_KEY[key] || 'erc4626';
  return EVENT_DEFS[type];
}

async function scanVaultDeposits({
  belt,
  provider,
  userAddress,
  onProgress,
  signal,
}) {
  const ethers = getEthers();
  const deployBlock = Number(belt.deployBlock || 0);
  const vaultAddress = belt.address;
  const chain = belt.chain;
  const userLower = normalizeAddress(ethers, userAddress);

  if (!deployBlock || deployBlock <= 0) {
    return {
      key: belt.key,
      label: belt.label,
      chain,
      vaultAddress,
      status: 'missing_deploy_block',
      lifetimeDepositedAssets: '0',
      lastScannedBlock: 0,
    };
  }

  const cacheKey = makeCacheKey(chain, vaultAddress, userLower);
  const cached = loadCache(cacheKey);
  let totalAssets = 0n;
  let lastScannedBlock = deployBlock - 1;

  if (cached?.lifetimeDepositedAssets) {
    try {
      totalAssets = ethers.toBigInt(cached.lifetimeDepositedAssets);
      lastScannedBlock = Number(cached.lastScannedBlock || lastScannedBlock);
    } catch {
      totalAssets = 0n;
      lastScannedBlock = deployBlock - 1;
    }
  }

  const startBlock = Math.max(deployBlock, lastScannedBlock + 1);
  const latestBlock = await provider.getBlockNumber();
  if (startBlock > latestBlock) {
    return {
      key: belt.key,
      label: belt.label,
      chain,
      vaultAddress,
      status: 'cached',
      lifetimeDepositedAssets: totalAssets.toString(),
      lastScannedBlock,
    };
  }

  const eventDef = resolveDepositEvent(belt);
  const iface = new ethers.Interface([eventDef.signature]);
  const topic0 = iface.getEvent(eventDef.name).topicHash;
  const ownerTopic = toOwnerTopic(ethers, userLower);

  let chunkSize = DEFAULT_CHUNK_SIZE;
  let currentFrom = startBlock;

  while (currentFrom <= latestBlock) {
    if (signal?.aborted) {
      return {
        key: belt.key,
        label: belt.label,
        chain,
        vaultAddress,
        status: 'aborted',
        lifetimeDepositedAssets: totalAssets.toString(),
        lastScannedBlock,
      };
    }

    const toBlock = Math.min(currentFrom + chunkSize - 1, latestBlock);
    const filter = {
      address: vaultAddress,
      fromBlock: currentFrom,
      toBlock,
      topics: eventDef.topicFilter(ownerTopic, topic0),
    };

    let logs;
    try {
      logs = await provider.getLogs(filter);
    } catch (err) {
      if (chunkSize <= MIN_CHUNK_SIZE) {
        throw err;
      }
      chunkSize = Math.max(MIN_CHUNK_SIZE, Math.floor(chunkSize / 2));
      continue;
    }

    for (const log of logs) {
      try {
        const parsed = iface.parseLog(log);
        const owner = normalizeAddress(ethers, parsed.args?.[eventDef.ownerField]);
        if (owner !== userLower) continue;
        const assets = ethers.toBigInt(parsed.args?.assets ?? 0);
        totalAssets += assets;
      } catch {
        // ignore parse errors
      }
    }

    lastScannedBlock = toBlock;
    saveCache(cacheKey, {
      version: 1,
      vaultAddress,
      chain,
      user: userLower,
      lifetimeDepositedAssets: totalAssets.toString(),
      lastScannedBlock,
      updatedAt: Date.now(),
    });

    onProgress?.({
      key: belt.key,
      label: belt.label,
      chain,
      status: 'scanning',
      lastScannedBlock,
      latestBlock,
    });

    currentFrom = toBlock + 1;
  }

  return {
    key: belt.key,
    label: belt.label,
    chain,
    vaultAddress,
    status: 'done',
    lifetimeDepositedAssets: totalAssets.toString(),
    lastScannedBlock,
  };
}

export function formatUsdcFromBaseUnits(raw) {
  const ethers = getEthers();
  try {
    return Number(ethers.formatUnits(ethers.toBigInt(raw || 0), USDC_DECIMALS));
  } catch {
    return 0;
  }
}

export async function scanBeltLifetimeDeposits({
  belts,
  provider,
  userAddress,
  onProgress,
  signal,
}) {
  const results = {};
  for (const belt of belts) {
    try {
      const res = await scanVaultDeposits({
        belt,
        provider,
        userAddress,
        onProgress,
        signal,
      });
      results[belt.key] = res;
      onProgress?.({
        key: belt.key,
        label: belt.label,
        chain: belt.chain,
        status: res.status,
        lastScannedBlock: res.lastScannedBlock,
        lifetimeDepositedAssets: res.lifetimeDepositedAssets,
      });
    } catch (err) {
      results[belt.key] = {
        key: belt.key,
        label: belt.label,
        chain: belt.chain,
        vaultAddress: belt.address,
        status: 'error',
        error: err?.message || String(err),
        lifetimeDepositedAssets: '0',
        lastScannedBlock: 0,
      };
    }
  }
  return results;
}
